import { Access, FeedbackStatusEnum, FeedbackTypeEnum } from "types";
import { SuggestionType } from "api/suggestions";
import { AgentStatus, AUTONOMOUS_AGENT_STATUS } from "constants/agents";

export const getStatusTranslationKey = (status: FeedbackStatusEnum): string => {
  const statusMap: Record<FeedbackStatusEnum, string> = {
    [FeedbackStatusEnum.PENDING]: "feedback.pending",
    [FeedbackStatusEnum.APPROVED]: "feedback.approved",
    [FeedbackStatusEnum.REJECTED]: "feedback.rejected",
  };
  return statusMap[status];
};

export const getTypeTranslationKey = (type: FeedbackTypeEnum): string => {
  const typeMap: Record<FeedbackTypeEnum, string> = {
    [FeedbackTypeEnum.CONTRIBUTE]: "feedback.contribute",
    [FeedbackTypeEnum.DISLIKE]: "feedback.dislike",
  };
  return typeMap[type];
};

export const getAccessTranslationKey = (access: Access): string => {
  const accessMap: Record<Access, string> = {
    ALLOW: "acl.allow",
    DENY: "acl.deny",
    NOT_SET: "acl.notSet",
  };
  return accessMap[access];
};

/**
 * Get accessOptions with i18n translations
 * This function should be used inside a component with useTranslation hook
 * @param t - The translation function from useTranslation hook
 * @returns Array of access options with translated labels
 */
export const getAccessOptionsWithI18n = (
  t: (key: string) => string
): { label: string; value: Access }[] => {
  return [
    { label: t(getAccessTranslationKey("ALLOW")), value: "ALLOW" },
    { label: t(getAccessTranslationKey("DENY")), value: "DENY" },
    { label: t(getAccessTranslationKey("NOT_SET")), value: "NOT_SET" },
  ];
};

export const getSuggestionTypeTranslationKey = (
  type: SuggestionType
): string => {
  const typeMap: Record<SuggestionType, string> = {
    [SuggestionType.INITIALIZE]: "suggestions.autoGenerated",
    [SuggestionType.FREQUENT]: "suggestions.frequent",
  };
  return typeMap[type];
};

export const getSubscriptionFrequencyTranslationKey = (
  frequency: string
): string => {
  const frequencyMap: Record<string, string> = {
    MONTHLY: "subscriptionPlans.monthly",
    YEARLY: "subscriptionPlans.yearly",
    WEEKLY: "subscriptionPlans.weekly",
    MANUALLY: "subscriptionPlans.manually",
  };
  return frequencyMap[frequency] || frequency;
};

export const getAgentStatusTranslationKey = (status: string): string => {
  // Check AgentStatus enum
  if (status === AgentStatus.TRAINING) return "agents.status.training";
  if (status === AgentStatus.ERROR) return "agents.status.error";
  if (status === AgentStatus.READY) return "agents.status.ready";
  
  // Check AUTONOMOUS_AGENT_STATUS enum
  if (status === AUTONOMOUS_AGENT_STATUS.SCHEDULING) return "agents.status.scheduling";
  if (status === AUTONOMOUS_AGENT_STATUS.IDLE) return "agents.status.idle";
  
  // Fallback to formatted status
  return status;
};
